import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AnalyticsData,
  DeviceAnalytics,
  ErrorData,
  GetAnalyticsDataRequest,
} from 'src/backend';
import { useGetAnalyticsMutation } from 'src/backend/api';
import ErrorAnalytics from 'src/pages/DeviceDetails/ErrorAnalytics';
import PageCard from 'src/components/PageCard';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import BasicAnalytics from './BasicAnalytics';

dayjs.extend(utc);

type Params = {
  id: string;
};

export default function DeviceDetailsAnalysis() {
  const { id } = useParams<Params>();
  const [getAnalytics, { isLoading }] = useGetAnalyticsMutation();
  const [fetchRequired, setFetchRequired] = useState(true);
  const [customDatesDisabled, setCustomDatesDisabled] = useState(true);

  const initialStartDate = dayjs.utc().subtract(28, 'd');
  const initialEndDate = dayjs.utc();
  const initialSamplingWindow = '1d';

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [samplingWindow, setSamplingWindow] = useState(initialSamplingWindow);

  const initialErrorData: ErrorData = {
    errorAnalytics: [],
    errorCodes: {},
  } as ErrorData;
  const [errorData, setErrorData] = useState(initialErrorData);

  const initiaAnalyticsData: AnalyticsData[] = [];
  const [analyticsData, setAnalyticsData] = useState(initiaAnalyticsData);

  const [timeRange, setTimeRange] = useState<string | null>('28-d/1d');

  const [includeSapImport, setIncludeSapImport] = useState(false);
  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeSapImport(event.target.checked);
  };

  function setDates(timeAgo: string) {
    const [amount, unit] = timeAgo.split('-');
    const units: 'd' | 'h' = unit as 'd' | 'h';
    const endDate = dayjs.utc();
    const startDate = dayjs.utc().subtract(Number(amount), units);

    setEndDate(endDate);
    setStartDate(startDate);
  }

  const fetchDataForCustomRange = useCallback(() => {
    const start = dayjs.utc(startDate);
    const end = dayjs.utc(endDate);
    const days = end.diff(start, 'd');
    if (days >= 7) {
      setSamplingWindow('1d');
    } else {
      setSamplingWindow('5m');
    }
    setFetchRequired(true);
  }, [startDate, endDate]);

  const onTimeRangeButtonChanged = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
      if (newAlignment === null) {
        return;
      }
      setTimeRange(newAlignment);
      if (newAlignment === 'Custom') {
        setCustomDatesDisabled(false);
      } else {
        setIncludeSapImport(false);
        setCustomDatesDisabled(true);
        const [timeAgo, samplingWindow] = newAlignment?.split('/') ?? [
          '24-h',
          '1d',
        ];
        setSamplingWindow(samplingWindow);
        setDates(timeAgo);
        setFetchRequired(true);
      }
    },
    [],
  );

  const setSeriesData = useCallback((data: DeviceAnalytics) => {
    setErrorData(data.errorData);
    setAnalyticsData(data.analyticsData);
  }, []);

  const fetchData = useCallback(() => {
    const now = new Date();
    const utcMidnight = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
    );
    const referenceDate = new Date(
      utcMidnight + now.getTimezoneOffset() * 60000,
    );
    const request: GetAnalyticsDataRequest = {
      id: `${id}`,
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      samplingWindow,
      includeSapImport,
      referenceDate,
    };

    getAnalytics({ id, analyticsDataRequest: request })
      .unwrap()
      .then((data) => {
        setSeriesData(data);
      });
  }, [
    endDate,
    setSeriesData,
    getAnalytics,
    id,
    samplingWindow,
    startDate,
    includeSapImport,
  ]);

  useEffect(() => {
    if (fetchRequired) {
      setFetchRequired(false);
      fetchData();
    }
  }, [fetchRequired, fetchData]);

  return (
    <PageCard title="Analysis" icon={<TimelineOutlinedIcon />}>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </div>
      ) : (
        <Grid>
          <div style={{ paddingLeft: '4em', paddingBottom: '3em' }}>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={onTimeRangeButtonChanged}
              aria-label="text alignment"
              style={{ marginRight: '10px' }}
            >
              <ToggleButton value="28-d/1d" aria-label="left aligned">
                28 Days
              </ToggleButton>
              <ToggleButton value="7-d/5m" aria-label="centered">
                7 Days
              </ToggleButton>
              <ToggleButton value="24-h/5m" aria-label="right aligned">
                24 Hours
              </ToggleButton>
              <ToggleButton value="1-h/5m" aria-label="right aligned">
                1 Hour
              </ToggleButton>
              <ToggleButton value="Custom" aria-label="justified" size="medium">
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                disabled={customDatesDisabled}
                label="Start date"
                value={startDate.local()}
                slotProps={{
                  textField: {
                    size: 'medium',
                    style: { marginLeft: '5px' },
                  },
                }}
                onChange={(newValue) => {
                  setStartDate(newValue ? newValue.utc() : dayjs.utc());
                }}
              />
              <DateTimePicker
                disabled={customDatesDisabled}
                label="End date"
                value={endDate.local()}
                defaultValue={endDate.local()}
                slotProps={{
                  textField: {
                    size: 'medium',
                    style: { marginLeft: '5px' },
                  },
                }}
                onChange={(newValue) => {
                  setEndDate(newValue ? newValue.utc() : dayjs.utc());
                }}
              />
            </LocalizationProvider>
            <FormControlLabel
              sx={{ marginLeft: '10px' }}
              label="Include SAP import"
              control={
                <Checkbox
                  checked={includeSapImport}
                  disabled={customDatesDisabled}
                  onChange={handleCheckChange}
                />
              }
            />
            <IconButton
              disabled={customDatesDisabled}
              onClick={() => fetchDataForCustomRange()}
            >
              {customDatesDisabled ? '' : <CheckOutlinedIcon />}
            </IconButton>
          </div>
          <ErrorAnalytics
            errorData={errorData}
            samplingWindow={samplingWindow}
          />
          <Divider />
          <BasicAnalytics
            analyticsData={analyticsData}
            samplingWindow={samplingWindow}
          />
        </Grid>
      )}
    </PageCard>
  );
}
